/**
 * The ⌘K palette's ACTION registry — things the palette can DO, as data
 * (mirrors the navItems registry precedent so actions are testable and the
 * palette component stays declarative). Every `run` is called AFTER the
 * palette has closed (`setOpen(false)` first), so actions that highlight DOM
 * (tours) defer via setTimeout until the dialog overlay has unmounted.
 *
 * Create-style actions work via DEEP LINKS (`?create=`, `?invite=1`) that the
 * target pages read in an effect — so they behave the same whether the admin
 * is elsewhere or already on the target page. Never host the heavy dialogs
 * here: the owning pages refresh their stores in onCreated.
 */
import type { LucideIcon } from 'lucide-react';
import {
  Bug,
  ClipboardList,
  CreditCard,
  Link as LinkIcon,
  MailPlus,
  MessageCircleQuestion,
  Settings,
  Trophy,
  CalendarDays,
  UserPlus,
} from 'lucide-react';
import { toast } from 'sonner';
import { TOURS } from '@/tours';
import type { TourId } from '@/store/tourStore';
import useAssistantStore from '@/store/assistantStore';
import { openFeedbackDialog } from '@/components/FeedbackDialog/FeedbackDialog';
import { publicSiteUrl } from '@/utils/publicSiteUrl';

export interface PaletteActionContext {
  navigate: (href: string) => void;
  leagueName: string;
  /** Absent while the admin record is still loading — tour actions no-op. */
  clerkId?: string;
  getToken: () => Promise<string | null>;
  markTourCompleted: (
    tourId: TourId,
    clerkId: string,
    getToken: () => Promise<string | null>,
  ) => void;
}

export interface PaletteAction {
  id: string;
  /** Visible label, e.g. "Create tournament". */
  name: string;
  /** Extra synonyms baked into the cmdk `value` for fuzzy matching. */
  keywords: string;
  Icon: LucideIcon;
  run: (ctx: PaletteActionContext) => void;
}

/** Same reason as TourLauncher's handleStartTour: the palette overlay must
    unmount before driver.js measures/highlights its first anchor. */
const TOUR_START_DELAY_MS = 300;

export function getPaletteActions(): PaletteAction[] {
  const actions: PaletteAction[] = [
    {
      id: 'create-tournament',
      name: 'Create tournament',
      keywords: 'new create tournament competition event bracket',
      Icon: Trophy,
      run: (ctx) => ctx.navigate('/admin/organize?create=tournament'),
    },
    {
      id: 'create-league',
      name: 'Create league',
      keywords: 'new create league season competition',
      Icon: CalendarDays,
      run: (ctx) => ctx.navigate('/admin/organize?create=league'),
    },
    {
      id: 'create-registration',
      name: 'Create registration',
      keywords: 'new create registration signup open form',
      Icon: ClipboardList,
      run: (ctx) => ctx.navigate('/admin/registration?create=tournament'),
    },
    {
      id: 'new-message',
      name: 'New message',
      keywords: 'new message broadcast email send announce',
      Icon: MailPlus,
      run: (ctx) => ctx.navigate('/admin/messages/new'),
    },
    {
      id: 'invite-admin',
      name: 'Invite admin',
      keywords: 'invite admin member add teammate staff user',
      Icon: UserPlus,
      run: (ctx) => ctx.navigate('/admin/members?invite=1'),
    },
    {
      id: 'copy-site-link',
      name: 'Copy public site link',
      keywords: 'copy public site link url website share',
      Icon: LinkIcon,
      run: (ctx) => {
        const url = publicSiteUrl(ctx.leagueName);
        // Clipboard needs a secure context — fall back to a readable error
        // instead of an unhandled rejection.
        try {
          navigator.clipboard
            .writeText(url)
            .then(() => toast.success('Public site link copied'))
            .catch(() => toast.error(`Couldn't copy. Your site is at ${url}`));
        } catch {
          toast.error(`Couldn't copy. Your site is at ${url}`);
        }
      },
    },
    {
      id: 'open-assistant',
      name: 'Ask the help assistant',
      keywords: 'help assistant ask ai support chat question how',
      Icon: MessageCircleQuestion,
      // Small defer so the palette's focus-restore doesn't fight the drawer.
      // (assistantStore is an untyped zustand store — hence the cast.)
      run: () => setTimeout(() => (useAssistantStore.getState() as any).open(), 100),
    },
    {
      id: 'report-feedback',
      name: 'Report a bug / request a feature',
      keywords: 'report bug broken issue problem feedback feature request idea suggest',
      Icon: Bug,
      // Same defer as the assistant: let the palette overlay unmount before
      // the dialog takes focus.
      run: () => setTimeout(() => openFeedbackDialog(), 100),
    },
    {
      id: 'payment-settings',
      name: 'Open payments',
      keywords: 'payments payouts stripe transactions fees revenue money',
      Icon: CreditCard,
      run: (ctx) => ctx.navigate('/admin/transactions'),
    },
    {
      id: 'org-settings',
      name: 'Open settings',
      keywords: 'organization settings preferences configure options',
      Icon: Settings,
      run: (ctx) => ctx.navigate('/admin/settings'),
    },
  ];

  // One "Start tour: …" action per registered tour. Deliberately NOT gated on
  // toursDismissed — dismissal hides the proactive surfaces (launcher popover,
  // dashboard banner); typing a tour's name into the palette is explicit
  // intent (documented in ADM-00-14).
  const tourActions: PaletteAction[] = TOURS.map((tour) => ({
    id: `tour-${tour.id}`,
    name: `Start tour: ${tour.name}`,
    keywords: `tour guide walkthrough learn how ${tour.description}`,
    Icon: tour.icon,
    run: (ctx) => {
      if (!ctx.clerkId) return;
      const { clerkId, getToken, markTourCompleted, navigate } = ctx;
      setTimeout(() => {
        tour.startTour(() => markTourCompleted(tour.id, clerkId, getToken), navigate);
      }, TOUR_START_DELAY_MS);
    },
  }));

  return [...actions, ...tourActions];
}
